import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { readError } from "@/lib/api";
import { useSiteImage } from "@/hooks/useSiteImages";

const MEDICAL_IMG_DEFAULT =
  "https://images.pexels.com/photos/7088834/pexels-photo-7088834.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900";

const Login = () => {
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", remember: false });
  const { login } = useAuth();
  const asideImg = useSiteImage("loginAside", MEDICAL_IMG_DEFAULT);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e?.target?.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setSubmitting(true);
    (async () => {
      try {
        await login({ email: form.email, password: form.password });
        toast.success("Welcome back.");
        navigate(from, { replace: true });
      } catch (err) {
        toast.error(readError(err) || "Sign in failed.");
      } finally {
        setSubmitting(false);
      }
    })();
  };

  return (
    <div
      className="relative min-h-[calc(100vh-5rem)] bg-white"
      data-testid="page-login"
    >
      <div className="mx-auto grid w-full max-w-7xl gap-10 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-12">
        <aside className="relative hidden lg:col-span-5 lg:block">
          <div className="sticky top-28">
            <div className="relative overflow-hidden rounded-[28px] shadow-2xl shadow-apicon-navy/20 ring-1 ring-black/5">
              <img
                src={asideImg}
                alt="Physician in consultation"
                className="h-[560px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-apicon-navy via-apicon-navy/50 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] backdrop-blur">
                  <Stethoscope className="h-3.5 w-3.5 text-apicon-gold" />
                  Delegate portal
                </div>
                <div className="mt-4 font-display text-3xl font-semibold leading-tight">
                  Welcome back, <br /> Doctor.
                </div>
                <p className="mt-3 max-w-xs text-sm text-white/80">
                  Sign in to manage your APICON 2026 registration, workshop
                  choices and delegate kit.
                </p>
              </div>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-xl"
          >
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
              Delegate sign-in
            </div>
            <h1
              className="mt-3 font-display text-4xl font-bold tracking-tight text-apicon-navy sm:text-5xl"
              data-testid="login-title"
            >
              Continue your <span className="italic text-apicon-green">journey</span>.
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-gray-600">
              Sign in with the email you registered with. New here?{" "}
              <Link
                to="/register"
                className="font-medium text-apicon-navy underline underline-offset-4 hover:text-apicon-green"
              >
                Create a delegate account
              </Link>
              .
            </p>

            <form
              onSubmit={onSubmit}
              className="mt-10 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm sm:p-8"
              data-testid="login-form"
            >
              <div className="grid gap-2">
                <Label htmlFor="login-email" className="text-sm text-apicon-navy">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@hospital.in"
                    value={form.email}
                    onChange={update("email")}
                    className="h-12 rounded-xl border-gray-200 pl-10 text-sm focus-visible:ring-apicon-navy"
                    data-testid="login-input-email"
                  />
                </div>
              </div>

              <div className="mt-5 grid gap-2">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="login-password"
                    className="text-sm text-apicon-navy"
                  >
                    Password
                  </Label>
                  <button
                    type="button"
                    className="text-xs font-medium text-apicon-navy hover:text-apicon-green"
                    data-testid="login-forgot-password"
                    onClick={() => navigate("/forgot-password")}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="login-password"
                    type={showPwd ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Your password"
                    value={form.password}
                    onChange={update("password")}
                    className="h-12 rounded-xl border-gray-200 pl-10 pr-10 text-sm focus-visible:ring-apicon-navy"
                    data-testid="login-input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:text-apicon-navy"
                    aria-label="Toggle password visibility"
                    data-testid="login-toggle-password"
                  >
                    {showPwd ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <Checkbox
                  id="remember"
                  checked={form.remember}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, remember: !!v }))
                  }
                  className="border-gray-300 data-[state=checked]:border-apicon-navy data-[state=checked]:bg-apicon-navy"
                  data-testid="login-checkbox-remember"
                />
                <Label
                  htmlFor="remember"
                  className="text-sm font-normal text-gray-600"
                >
                  Remember me on this device
                </Label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="mt-8 h-12 w-full rounded-full bg-apicon-navy text-base font-semibold text-white shadow-lg shadow-apicon-navy/20 hover:bg-[#082C5A] disabled:opacity-70"
                data-testid="login-submit"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </Button>

              <div className="my-8 flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-gray-400">
                <span className="h-px flex-1 bg-gray-200" />
                <span>Or</span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>

              <Link to="/register" className="block" data-testid="login-register-cta">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 w-full rounded-full border-apicon-navy/20 text-apicon-navy hover:bg-apicon-mist hover:text-apicon-navy"
                >
                  Create a new delegate account
                </Button>
              </Link>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
